/**
 * AlertManagerStyleLoader Component
 * 
 * Dynamically loads the appropriate CSS styles for the AlertManager component
 * based on the current theme (dark or light).
 * 
 * This follows the centralized CSS theming approach where theme-specific styles
 * are stored in separate files and loaded dynamically.
 * 
 * It also ensures proper CSS scoping by wrapping children in a styled container.
 */

import React, { useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { THEMES } from '../../context/ThemeContext';

/**
 * AlertManagerStyleLoader component
 * Loads the appropriate CSS styles for the AlertManager component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The children with styles applied
 */
function AlertManagerStyleLoader({ children }) {
  const { theme } = useTheme();
  
  useEffect(() => {
    // Dynamically import the CSS files based on the current theme
    const loadStyles = async () => {
      if (theme === THEMES.DARK) {
        // Load dark theme styles
        await import('../../styles/themes/dark/AlertManager/AlertManager.css');
      } else {
        // Load light theme styles
        await import('../../styles/themes/light/AlertManager/AlertManager.css');
      }
    };
    
    loadStyles();
  }, [theme]);
  
  // Create a wrapper div with the appropriate class to ensure CSS scoping works
  return (
    <div className="alert-manager-style-wrapper" data-theme={theme}>
      {children}
    </div>
  );
}

export default AlertManagerStyleLoader;
